import {DEFAULT_HEALTH_URL, MAX_HEALTH_CHECKS_PER_REQUEST} from './constants';

function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasDebugCookie() {
    return typeof document !== 'undefined' && document.cookie.indexOf('debug=1') !== -1;
}

function debugLog(...args) {
    if (hasDebugCookie() && typeof console !== 'undefined' && typeof console.log === 'function') {
        console.log('[MetrikaHealth]', ...args);
    }
}

function debugGroup(title, payload, callback) {
    if (!hasDebugCookie() || typeof console === 'undefined') {
        return callback();
    }

    const canGroup = typeof console.groupCollapsed === 'function' && typeof console.groupEnd === 'function';

    if (!canGroup) {
        debugLog(title, payload);
        return callback();
    }

    console.groupCollapsed('[MetrikaHealth]', title, payload);

    try {
        const result = callback();

        if (result && typeof result.then === 'function') {
            return result.finally(() => {
                console.groupEnd();
            });
        }

        console.groupEnd();
        return result;
    } catch (error) {
        console.groupEnd();
        throw error;
    }
}

export function normalizeHealthChecks(checks) {
    const items = Array.isArray(checks) ? checks : [checks];
    const normalized = [];

    for (const item of items) {
        if (isPlainObject(item)) {
            normalized.push(item);
        }
    }

    return normalized;
}

/**
 * Sends provider health checks to /collect/health/.
 * @param {import('./metrika-collector').default} collector
 * @param {Array<object>|object} checks
 * @param {object} [options]
 * @param {string} [options.endpoint]
 * @param {string|null} [options.visitorId]
 * @param {boolean} [options.useBeacon]
 * @returns {Promise<boolean>}
 */
export async function sendMetricHealth(collector, checks, options = {}) {
    const normalizedChecks = normalizeHealthChecks(checks).slice(0, MAX_HEALTH_CHECKS_PER_REQUEST);

    if (normalizedChecks.length === 0) {
        debugLog('skip empty health batch');
        return false;
    }

    const payload = {
        checks: normalizedChecks,
    };

    const visitorId = options.visitorId ?? (
        collector && typeof collector.getVisitorId === 'function'
            ? collector.getVisitorId()
            : null
    );

    if (visitorId) {
        payload.visitor_id = visitorId;
    }

    const endpoint = options.endpoint || (collector && collector.healthEndpoint) || DEFAULT_HEALTH_URL;
    const body = JSON.stringify(payload);

    return debugGroup('send', {
        endpoint,
        checks: normalizedChecks.length,
        visitorId: Boolean(visitorId),
        useBeacon: Boolean(options.useBeacon),
    }, async () => {
        if (
            options.useBeacon
            && typeof navigator !== 'undefined'
            && typeof navigator.sendBeacon === 'function'
        ) {
            const sent = navigator.sendBeacon(endpoint, new Blob([body], {type: 'application/json'}));
            debugLog('sent via beacon', {checks: normalizedChecks.length, sent});

            return sent;
        }

        const headers = {'Content-Type': 'application/json'};
        const debug = Boolean(options.debug ?? (collector && collector.debug));

        if (debug) {
            headers['X-Metrika-Debug'] = '1';
        }

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body,
                keepalive: true,
            });

            debugLog('response', {
                status: response.status,
                ok: response.ok,
                checks: normalizedChecks.length,
            });

            return response.ok;
        } catch (error) {
            debugLog('fetch failed', {
                message: error && error.message ? error.message : String(error),
                checks: normalizedChecks.length,
            });

            return false;
        }
    });
}

export function collectHealth(collector, checks, options = {}) {
    if (collector && typeof collector.collectHealth === 'function') {
        return collector.collectHealth(checks, options);
    }

    return sendMetricHealth(collector, checks, options);
}
